import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Home } from "lucide-react";

export default function PaymentReturn() {
  const location = useLocation();
  const navigate = useNavigate();
  const isCancel = location.pathname.includes("cancel");
  const [status, setStatus] = useState(isCancel ? "cancelled" : "checking");
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (isCancel) return;
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    const kind = params.get("type") || "payment";
    if (!sid) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(kind === "autopay" ? `/tenant/autopay/status/${sid}` : `/payments/status/${sid}`);
        setAmount(data.amount);
        if (data.payment_status === "paid") { setStatus(kind === "autopay" ? "autopay" : "paid"); return; }
        if (data.status === "expired") { setStatus("error"); return; }
      } catch (err) { console.warn("Payment status check failed, retrying", err?.message); }
      if (tries++ < 8) setTimeout(poll, 1600);
      else setStatus("pending");
    };
    poll();
  }, [location, isCancel]);

  const Card = ({ icon: Icon, color, title, msg }) => (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center animate-fade-up">
        <div className={`mx-auto h-16 w-16 rounded-2xl flex items-center justify-center mb-5 ${color}`}><Icon className="h-8 w-8" /></div>
        <h1 className="font-display font-extrabold text-2xl text-slate-900">{title}</h1>
        <p className="text-sm text-slate-500 mt-2">{msg}</p>
        <Button onClick={() => navigate("/portal/payments")} className="mt-6 gap-2" data-testid="return-home-btn"><Home className="h-4 w-4" /> Back to portal</Button>
      </div>
    </div>
  );

  if (status === "checking")
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-slate-500">
        <Loader2 className="h-7 w-7 animate-spin text-primary" /> Confirming your payment…
      </div>
    );
  if (status === "paid")
    return <Card icon={CheckCircle2} color="bg-emerald-100 text-emerald-600" title="Payment successful!" msg={`${amount ? fmtMoney(amount) + " " : ""}has been received and recorded in your ledger. Thank you!`} />;
  if (status === "autopay")
    return <Card icon={CheckCircle2} color="bg-emerald-100 text-emerald-600" title="Auto-pay is on!" msg={`${amount ? fmtMoney(amount) + " " : "Your copay "}will be charged to your card automatically on your rent due day each month. Nothing was charged today.`} />;
  if (status === "pending")
    return <Card icon={Loader2} color="bg-amber-100 text-amber-600" title="Payment processing" msg="Your payment is being processed. It will appear in your ledger shortly." />;
  if (status === "cancelled")
    return <Card icon={XCircle} color="bg-slate-100 text-slate-500" title="Payment cancelled" msg="No charge was made. You can try again whenever you're ready." />;
  return <Card icon={XCircle} color="bg-rose-100 text-rose-600" title="Something went wrong" msg="We couldn't confirm this payment. Please try again or contact your landlord." />;
}
